// Pointer / wheel / keyboard interaction for the minimap canvas.
//
//   click              -> onTileClick(q, r) on a non-fog tile (the App flies the 3D camera there)
//   drag (primary)     -> move the 3D CAMERA: onCameraDrag(from, to) in unit space (M2; the App
//                         turns it into a grab-pan of the orbit target)
//   shift+drag /       -> pan the MINIMAP viewport (the old drag behaviour)
//   middle-drag
//   wheel / pinch      -> zoom the MINIMAP viewport about the cursor / pinch centre (not the camera)
//   keyboard           -> arrows move a focus hex, Enter = click, + / - zoom, 0 fits

import {
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { PreparedBoard } from './minimapBoard.js';
import type { Point } from './minimapGeometry.js';
import { useElementSize, useMinimapKeyboardFocus, useWheelZoom } from './minimapHooks.js';
import {
  type FocusState,
  type GestureState,
  type HoverState,
  type PointerRec,
  dragMode,
  hitTest,
} from './minimapPointer.js';
import {
  type CanvasSize,
  type Viewport,
  ZOOM_STEP,
  clampPan,
  fitBoard,
  panBy,
  screenToUnit,
  zoomAt,
  zoomLevel,
} from './minimapViewport.js';

const DRAG_THRESHOLD = 4;

export interface UseMinimapInteraction {
  readonly containerRef: React.RefObject<HTMLDivElement | null>;
  readonly canvasRef: React.RefObject<HTMLCanvasElement | null>;
  readonly size: CanvasSize;
  readonly viewport: Viewport | null;
  readonly zoom: number;
  readonly fitted: boolean;
  readonly hover: HoverState | null;
  readonly focus: FocusState | null;
  readonly focused: boolean;
  readonly zoomIn: () => void;
  readonly zoomOut: () => void;
  readonly fit: () => void;
  /** Dismiss the hover/tap chip (the phone chip fades on its own). */
  readonly clearHover: () => void;
  readonly handlers: {
    readonly onPointerDown: (e: ReactPointerEvent<HTMLCanvasElement>) => void;
    readonly onPointerMove: (e: ReactPointerEvent<HTMLCanvasElement>) => void;
    readonly onPointerUp: (e: ReactPointerEvent<HTMLCanvasElement>) => void;
    readonly onPointerCancel: (e: ReactPointerEvent<HTMLCanvasElement>) => void;
    readonly onPointerLeave: (e: ReactPointerEvent<HTMLCanvasElement>) => void;
    readonly onKeyDown: (e: ReactKeyboardEvent<HTMLCanvasElement>) => void;
    readonly onFocus: () => void;
    readonly onBlur: () => void;
  };
}

export function useMinimapInteraction(opts: {
  board: PreparedBoard;
  onTileClick?: (q: number, r: number) => void;
  /**
   * Primary-button drag (one finger on touch): move the 3D camera. Called with the
   * unit-space points under the pointer at drag start and now, so the App can keep
   * the grabbed ground point under the cursor. Absent = drag pans the viewport.
   */
  onCameraDrag?: (from: Point, to: Point) => void;
  /**
   * A camera drag ended (pointer up or cancel). The host drops the grab so the
   * next drag starts a fresh one.
   */
  onCameraDragEnd?: () => void;
  /**
   * The local player's own cities, for the `C` key (spec §5: "C cycles own cities").
   * Ordered so repeated presses visit them in a stable order.
   */
  ownCities?: readonly { q: number; r: number }[];
}): UseMinimapInteraction {
  const { board, onTileClick, onCameraDrag, onCameraDragEnd, ownCities } = opts;
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const size = useElementSize(containerRef);
  // null = "fit the board" (follows resizes); otherwise a user-chosen viewport.
  const [custom, setCustom] = useState<Viewport | null>(null);
  const [hover, setHover] = useState<HoverState | null>(null);

  const hasSize = size.width > 0 && size.height > 0;
  const viewport = useMemo<Viewport | null>(() => {
    if (!hasSize) return null;
    return custom ? clampPan(custom, board.bounds, size) : fitBoard(board.bounds, size);
  }, [custom, hasSize, board.bounds, size]);

  const vpRef = useRef(viewport);
  vpRef.current = viewport;
  const boardRef = useRef(board);
  boardRef.current = board;
  const sizeRef = useRef(size);
  sizeRef.current = size;
  const clickRef = useRef(onTileClick);
  clickRef.current = onTileClick;
  const cameraDragRef = useRef(onCameraDrag);
  cameraDragRef.current = onCameraDrag;
  const cameraDragEndRef = useRef(onCameraDragEnd);
  cameraDragEndRef.current = onCameraDragEnd;
  const ownCitiesRef = useRef(ownCities);
  ownCitiesRef.current = ownCities;

  const zoomBy = useCallback((factor: number, anchor?: { x: number; y: number }) => {
    const vp = vpRef.current;
    const sz = sizeRef.current;
    if (!vp) return;
    const a = anchor ?? { x: sz.width / 2, y: sz.height / 2 };
    setCustom(zoomAt(vp, factor, a, boardRef.current.bounds, sz));
  }, []);

  const zoomIn = useCallback(() => zoomBy(ZOOM_STEP), [zoomBy]);
  const zoomOut = useCallback(() => zoomBy(1 / ZOOM_STEP), [zoomBy]);
  const fit = useCallback(() => setCustom(null), []);
  const clearHover = useCallback(() => setHover(null), []);

  const { focus, focused, onKeyDown, onFocus, onBlur } = useMinimapKeyboardFocus({
    boardRef,
    vpRef,
    sizeRef,
    setCustom,
    clickRef,
    zoomBy,
    ownCitiesRef,
  });

  // Non-passive wheel listener so the page does not scroll while zooming the map.
  useWheelZoom(canvasRef, zoomBy);

  const pointers = useRef(new Map<number, PointerRec>());
  const gesture = useRef<GestureState>({
    dragged: false,
    startX: 0,
    startY: 0,
    pinchDist: 0,
    pinchCentre: null,
    multi: false,
    mode: 'camera',
    button: 0,
    startUnit: null,
  });

  const local = useCallback((e: { clientX: number; clientY: number }) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    return { x: e.clientX - (rect?.left ?? 0), y: e.clientY - (rect?.top ?? 0) };
  }, []);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLCanvasElement>) => {
      const p = local(e);
      pointers.current.set(e.pointerId, p);
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* synthetic events have no active pointer */
      }
      const g = gesture.current;
      if (pointers.current.size === 1) {
        g.dragged = false;
        g.multi = false;
        g.startX = p.x;
        g.startY = p.y;
        g.button = e.button;
        g.mode = dragMode(e, cameraDragRef.current !== undefined);
        g.startUnit = null;
      } else {
        g.multi = true;
        const [a, b] = [...pointers.current.values()];
        g.pinchDist = a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
        g.pinchCentre = a && b ? { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } : null;
      }
    },
    [local],
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLCanvasElement>) => {
      const p = local(e);
      const vp = vpRef.current;
      if (!vp) return;
      const prev = pointers.current.get(e.pointerId);
      const g = gesture.current;
      if (prev && pointers.current.size >= 2) {
        // Pinch: zoom about the centre of the two pointers, and pan the viewport
        // with the centre's motion. The pan matters on phones: one finger moves
        // the 3D camera, so two fingers are the only way to move the minimap
        // viewport itself once it is zoomed in.
        const before = [...pointers.current.values()];
        pointers.current.set(e.pointerId, p);
        const [a, b] = [...pointers.current.values()];
        if (!a || !b || before.length < 2) return;
        const dist = Math.hypot(a.x - b.x, a.y - b.y);
        const centre = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        if (g.pinchDist > 0 && dist > 0) {
          zoomBy(dist / g.pinchDist, centre);
        }
        if (g.pinchCentre) {
          const dx = centre.x - g.pinchCentre.x;
          const dy = centre.y - g.pinchCentre.y;
          if (dx !== 0 || dy !== 0) {
            setCustom(panBy(vp, dx, dy, boardRef.current.bounds, sizeRef.current));
          }
        }
        g.pinchDist = dist;
        g.pinchCentre = centre;
        g.dragged = true;
        return;
      }
      if (prev) {
        pointers.current.set(e.pointerId, p);
        if (!g.dragged && Math.hypot(p.x - g.startX, p.y - g.startY) > DRAG_THRESHOLD) {
          g.dragged = true;
          if (g.mode === 'camera') {
            const u = screenToUnit(vp, g.startX, g.startY);
            g.startUnit = { x: u.x, y: u.y };
          }
        }
        if (g.dragged) {
          if (g.mode === 'camera' && g.startUnit) {
            const u = screenToUnit(vp, p.x, p.y);
            cameraDragRef.current?.(g.startUnit, { x: u.x, y: u.y });
          } else {
            const sz = sizeRef.current;
            setCustom(panBy(vp, p.x - prev.x, p.y - prev.y, boardRef.current.bounds, sz));
          }
          setHover(null);
        }
        return;
      }
      // Plain hover (mouse / pen).
      if (e.pointerType === 'touch') return;
      const hit = hitTest(boardRef.current, vp, p.x, p.y);
      setHover((h) =>
        hit
          ? h &&
            h.q === hit.q &&
            h.r === hit.r &&
            Math.abs(h.x - p.x) < 2 &&
            Math.abs(h.y - p.y) < 2
            ? h
            : { q: hit.q, r: hit.r, x: p.x, y: p.y, touch: false }
          : null,
      );
    },
    [local, zoomBy],
  );

  const finish = useCallback(
    (e: ReactPointerEvent<HTMLCanvasElement>, cancelled: boolean) => {
      const g = gesture.current;
      const had = pointers.current.delete(e.pointerId);
      if (g.dragged && g.mode === 'camera') {
        // The camera drag is over: tell the host so it can drop the grab. A new
        // drag has to start a fresh one, or it would move the camera relative
        // to the previous drag's start.
        cameraDragEndRef.current?.();
      }
      if (!had || cancelled || g.dragged || g.multi) {
        if (pointers.current.size === 0) {
          g.multi = false;
          g.pinchCentre = null;
          g.pinchDist = 0;
        }
        return;
      }
      const vp = vpRef.current;
      if (!vp) return;
      const p = local(e);
      const hit = hitTest(boardRef.current, vp, p.x, p.y);
      if (!hit) return;
      if (e.pointerType === 'touch') {
        setHover({ q: hit.q, r: hit.r, x: p.x, y: p.y, touch: true });
      }
      if (hit.tile.visibility !== 'fog') clickRef.current?.(hit.q, hit.r);
    },
    [local],
  );

  const onPointerUp = useCallback(
    (e: ReactPointerEvent<HTMLCanvasElement>) => finish(e, false),
    [finish],
  );
  const onPointerCancel = useCallback(
    (e: ReactPointerEvent<HTMLCanvasElement>) => finish(e, true),
    [finish],
  );
  const onPointerLeave = useCallback((e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (e.pointerType !== 'touch') setHover(null);
  }, []);

  const zoom = viewport ? zoomLevel(viewport, board.bounds, size) : 1;

  return {
    containerRef,
    canvasRef,
    size,
    viewport,
    zoom,
    fitted: custom === null,
    hover,
    focus,
    focused,
    zoomIn,
    zoomOut,
    fit,
    clearHover,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
      onPointerLeave,
      onKeyDown,
      onFocus,
      onBlur,
    },
  };
}
